import { ISS_CLASSIFICATION_LABELS } from '../../helpers/sectorInsights/fetchInsightCells'
import getInsightNextTierGaps from '../../helpers/sectorInsights/getInsightNextTierGaps'
import { activityTypeLabels, activitySubtypeLabels } from '../../helpers/dataHelpers/activityCategories'
import { mechanismCategoryLabels } from '../../helpers/dataHelpers/mechanismCategories'
import segmentLabel from '../../helpers/intelligence/segmentLabel'

/**
 * Bij welke doelgroep deze cel gemeten is. Een segmentcel telt alleen de
 * respondenten die via hun onderzoek aan die doelgroep hangen, dus hij staat
 * naast de ongesegmenteerde cel met dezelfde dimensie en hetzelfde onderwerp
 * maar andere aantallen. Zonder deze markering lijken dat twee keer dezelfde
 * rij met tegenstrijdige cijfers. Een onbekende sleutel toont de sleutel zelf:
 * niets tonen zou de rij als ongesegmenteerd laten lezen.
 *
 * @param {object} props
 * @param {string|null|undefined} props.segment - Doelgroepsleutel; leeg = ongesegmenteerd, rendert niets
 */
const SegmentNote = ({ segment }) => (segment ? (
    <span className='sector-insight-segment'>
        alleen gemeten bij {segmentLabel(segment)}
    </span>
) : null)

const score = (value) => (typeof value === 'number' ? value.toFixed(2) : '—')

/**
 * Grijze hint bij een metriek: hoeveel die kolom moet veranderen om de cel één
 * classificatietrede te tillen. Rendert niets als deze kolom niet de bindende
 * poort is.
 *
 * @param {object} props
 * @param {number|null|undefined} props.value - Positief tekort tot de volgende trede; null/undefined toont niets.
 * @param {number} [props.decimals] - Aantal decimalen (0 voor tellingen, 2 voor B).
 */
const NextTierGapNote = ({ value, decimals = 0 }) => {
    if (value == null) return null
    return (
        <span className='sector-insight-gap-note'>
            {` (+${value.toFixed(decimals)})`}
        </span>
    )
}

/** Dimensie-type → menselijk label voor de "Dimensie"-kolom. */
const DIMENSION_TYPE_LABELS = {
    activity: 'Activiteit',
    mechanismCategory: 'Mechanisme',
    mechanismSignal: 'Mechanisme',
    context: 'Context',
}

/**
 * UI-vriendelijke activiteitstitel uit de taxonomie; valt terug op de ruwe code
 * als er (nog) geen label is.
 * @param {{activityType?: string, activitySubtype?: string|null}} cell
 * @returns {string}
 */
const activityLabel = (cell) => {
    const type = activityTypeLabels[cell.activityType] || cell.activityType
    const subtype = cell.activitySubtype
        ? activitySubtypeLabels[cell.activitySubtype] || cell.activitySubtype
        : ''
    return subtype ? `${type} — ${subtype}` : type
}

/**
 * Onderwerp-label per dimensie (UI-vriendelijk waar een woordenboek bestaat;
 * anders het server-label of de ruwe waarde).
 * @param {{dimension?: string, dimensionValue?: string, dimensionLabel?: string, activityType?: string, activitySubtype?: string|null}} cell
 * @returns {string}
 */
const subjectDisplay = (cell) => {
    switch (cell.dimension) {
        case 'mechanismCategory':
            return mechanismCategoryLabels[cell.dimensionValue] || cell.dimensionValue
        case 'mechanismSignal':
            return cell.dimensionValue // Title is al Nederlands
        case 'context':
            return cell.dimensionLabel || cell.dimensionValue
        case 'activity':
        default:
            // activiteit (of oude cellen zonder dimensie) → taxonomie-labels
            return cell.activityType ? activityLabel(cell) : (cell.dimensionLabel || '—')
    }
}

/**
 * @typedef {object} InsightCell
 * @property {string} cellId - stabiele celsleutel (§8-contract)
 * @property {string} activityType - activiteitstype van de cel
 * @property {string|null} activitySubtype - subtype, of null
 * @property {string} classification - ISS 2.0-classificatie
 * @property {string|null} design - meetdesign ('gekoppeld'|'gemengd'|'ongepaard')
 * @property {{paired: number, unpaired: number}|null} [respondentsByPath] - teltallen per meetpad
 * @property {number|null} B - bewijskracht
 * @property {number|null} E - effectgrootte
 * @property {number} nRespondents - aantal respondenten in de cel
 * @property {number} nOrgs - aantal organisaties
 * @property {number} meetronden - aantal meetronden
 * @property {{p3?: string}} [gates] - poortstatus (p3 = replicatie)
 */

/**
 * Pure tabel van ISS 2.0-celdocumenten (Insight Strength Score) voor één
 * MSI-effect. Gedeeld door de SuperTenant-editor (BenchmarkInsightDetails) en de
 * MSI-benchmark-monitor-accordion, zodat het recept op één plek leeft.
 *
 * De statistische parameters worden recht uit het scoredocument gerenderd —
 * curatiewaarborg (b): nooit uit de (herschreven) redactietekst.
 *
 * AI-CONTEXT: See docs/features/insight-strength-score-2.md
 *
 * @param {object} props
 * @param {Array<InsightCell>} props.cells - ISS-celdocumenten (§8-contract)
 * @param {boolean} [props.showNextTierGaps] - Toon per bindende kolom de grijze "wat is nog nodig voor de volgende trede"-hint (alleen de monitor-accordion).
 * @param {{level?: number, orgs?: number, respondents?: number, strong?: {level?: number, orgs?: number, respondents?: number}}} [props.p2Targets] - De P2-drempels zoals de SERVER ze meestuurt (`p2Targets` uit getInsightCells/getCellDetail). Zonder dit valt getInsightNextTierGaps terug op zijn eigen constanten, en die lopen uit de pas zodra de staffel wijzigt — dat is één keer misgegaan (de UI toonde de afstand tot de oude poort). Geef ze dus mee waar je ze hebt.
 * @param {(cell: InsightCell) => void} [props.onClassificationClick] - Maakt de classificatie-cel klikbaar (opent de uitleg-popup); niet gezet ⇒ niet klikbaar.
 */
const InsightCellsTable = ({ cells, showNextTierGaps = false, p2Targets = null, onClassificationClick }) => (
    <table className='sector-insight-details-table'>
        <thead>
            <tr>
                <th>Dimensie</th>
                <th>Onderwerp</th>
                <th>Classificatie</th>
                <th>Design</th>
                <th>B</th>
                <th>E</th>
                <th>n</th>
                <th>Organisaties</th>
                <th>Meetronden</th>
                <th>Replicatie</th>
            </tr>
        </thead>
        <tbody>
            {cells.map((cell) => {
                const gaps = showNextTierGaps ? getInsightNextTierGaps(cell, p2Targets) : null
                return (
                    <tr key={cell.cellId}>
                        <td>{DIMENSION_TYPE_LABELS[cell.dimension] || 'Activiteit'}</td>
                        <td>{subjectDisplay(cell)}<SegmentNote segment={cell.segment} /></td>
                        <td
                            className={onClassificationClick ? 'sector-insight-classification-cell' : ''}
                            onClick={onClassificationClick ? () => onClassificationClick(cell) : undefined}
                        >
                            {ISS_CLASSIFICATION_LABELS[cell.classification] || cell.classification}
                            {gaps?.awaitingContrast && (
                                <span className='sector-insight-gap-note'> (wacht op verschil met zonder)</span>
                            )}
                        </td>
                        <td>
                            {cell.design
                                ? `${cell.design}${cell.respondentsByPath
                                    ? ` (${cell.respondentsByPath.paired} gekoppeld · ${cell.respondentsByPath.unpaired} ongepaard)`
                                    : ''}`
                                : '—'}
                        </td>
                        <td>{score(cell.B)}<NextTierGapNote value={gaps?.B} decimals={2} /></td>
                        <td>{score(cell.E)}</td>
                        <td>{cell.nRespondents}<NextTierGapNote value={gaps?.nRespondents} /></td>
                        <td>{cell.nOrgs}<NextTierGapNote value={gaps?.nOrgs} /></td>
                        <td>{cell.meetronden}</td>
                        <td>
                            {cell.gates && cell.gates.p3 ? cell.gates.p3.replace(/_/g, ' ') : '—'}
                            {gaps?.awaitingReplication && (
                                <span className='sector-insight-gap-note'> (wacht op replicatie)</span>
                            )}
                        </td>
                    </tr>
                )
            })}
        </tbody>
    </table>
)

export default InsightCellsTable
